'use client'

import {
  Plus,
  FolderOpenDot,
  CreditCardIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  LogOutIcon,
  MailIcon,
  Globe2Icon,
  MapIcon,
  MessageSquareIcon,
  ClipboardListIcon,
  HelpCircleIcon,
  ChevronUpIcon,
  Store,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { GradientButton } from '@/components/gradient-button'
import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import React from 'react'
import { useRouter } from 'next/navigation'
import { useClickAway } from 'react-use'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  CHANGELOG_URL,
  EMAIL_URL,
  FEATUREBASE_URL,
  HELP_URL,
  MIN_COIN_FOR_CODING,
  ROADMAP_URL,
} from '@/lib/constant'
import sendToMixpanel from '@/lib/mixpanel/sendToMixpanel'
import { logoutAccount } from '@/lib/db/user'
import type { User } from '@/lib/types'
import Link from 'next/link'
import { SUBSCRIPTION_PRODUCTS } from '@/lib/plans'
import { ProjectList } from './project-list'
import { UserHeader } from '@/components/common/user-header'
import { pushModal } from '@/components/modals'
import { MyInviteCode } from '@/components/offer/my-invite-code'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import { useGlobalStore } from '@/hooks/use-global-store'
import { GetFreeCreditButton2 } from '@/components/get-free-credit-button'

// 添加 ListItem 组件
const ListItem = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<'a'>
>(({ className, children, ...props }, ref) => (
  <a
    ref={ref}
    className={cn(
      'block select-none rounded-md p-2 leading-none',
      'hover:bg-gray-800 transition-colors',
      'flex items-center gap-2',
      className,
    )}
    {...props}
  >
    {children}
  </a>
))
ListItem.displayName = 'ListItem'

interface ISidebarMenuBtn {
  currentProjectId?: string
  user: User | null
  // 是否透明背景
  transparent?: boolean
}

export const SidebarMenuBtn = ({
  currentProjectId,
  user,
  transparent = false,
}: ISidebarMenuBtn) => {
  const [open, setOpen] = useState(false)
  const [isProfilePopupOpened, setIsProfilePopupOpened] = useState(false)
  const [isProjectListOpen, setIsProjectListOpen] = useState(false)
  const t = useTranslations('project-preview')
  const tProfileMenu = useTranslations('profile-menu')
  const router = useRouter()
  const ref = React.useRef(null)
  const isAnyAlertDialogOpen = useGlobalStore(
    (state) => state.isAnyAlertDialogOpen,
  )

  const pricingPlanIcon = useMemo(() => {
    if (!user?.subscription?.product_id) {
      return null
    }
    const subscription = SUBSCRIPTION_PRODUCTS.find(
      (product) => product.id === user.subscription?.product_id,
    )
    if (!subscription) {
      return null
    }
    return (
      <img
        src={subscription.icon}
        alt={subscription.title}
        className="size-4"
      />
    )
  }, [user?.subscription?.product_id])

  useClickAway(ref, (e) => {
    e.stopPropagation()
    if (isAnyAlertDialogOpen) return
    if (isProfilePopupOpened) {
      setIsProfilePopupOpened(false)
    } else if (open) {
      setOpen(false)
    }
  })

  async function onLogout() {
    const success = await logoutAccount()
    if (success) {
      router.push('/login')
    }
    sendToMixpanel(
      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_LOGOUT,
      {},
      user?._id.toString(),
    )
  }

  if (!user) {
    return null
  }

  return (
    <>
      <UserHeader user={user} onClick={() => setOpen(true)} />
      <div
        ref={ref}
        className={cn(
          'fixed right-0 top-0 h-[calc(100vh-6em)] w-[330px] text-lg z-50',
          'bg-transparent',
          'transition-transform duration-300 ease-in-out',
          open ? 'translate-x-0' : 'translate-x-[105%]',
        )}
      >
        <div
          className={cn('h-16 bg-transparent')}
          onClick={() => setOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setOpen(false)
            }
          }}
        />
        <div
          className={cn(
            'flex flex-col h-full flex-1 shadow-[4px_0_24px_rgba(0,0,0,0.1)]',
            'rounded-lg drop-shadow-lg mr-4 py-6 gap-2',
            transparent ? 'bg-gray-800' : 'bg-gray-900/95',
          )}
        >
          <button
            className="w-full flex flex-col items-center bg-transparent border-0 px-4 py-2"
            onClick={() => {
              pushModal('CreditBalanceModal')
              // add mixpanel event
              sendToMixpanel(
                MIXPANEL_CLIENT_EVENT_NAMES.CLICK_PROFILE_MENU_AVATAR,
                { project_id: currentProjectId },
                user?._id.toString() || '',
              )
            }}
            type="button"
          >
            <div className="w-full flex items-center gap-2  bg-transparent text-white h-14 cursor-pointer">
              <div className="size-15 rounded-full bg-brand flex items-center justify-center text-white text-3xl font-medium uppercase">
                {user.email.slice(0, 2)}
              </div>
              <div className="text-sm truncate flex-1 text-white font-semibold gap-2 flex flex-col justify-start">
                <span className="flex-1 flex justify-start">{user.email}</span>
                <div className="text-sm font-normal text-gray-300 flex items-center gap-1">
                  <div>{pricingPlanIcon}</div>
                  {user.coin < MIN_COIN_FOR_CODING
                    ? tProfileMenu('out_of_credit')
                    : `${user.coin?.toFixed(2) || '0.00'} ${tProfileMenu('credits')}`}
                </div>
                {/* <MyInviteCode className="mb-2" /> */}
              </div>
              <ChevronRightIcon
                className={cn(
                  'size-5 transition-transform shrink-0 text-white/80',
                )}
              />
            </div>
          </button>
          <div className="mt-2 px-4">
            <GetFreeCreditButton2 />
          </div>
          <div className="bg-white/10 h-px" />
          <ScrollArea className="flex-1">
            <div className="py-2 px-4 space-y-2">
              <GradientButton
                onClick={() => {
                  window.open('/', '_blank')
                }}
                className="w-full inline-flex items-center gap-1 text-xs rounded-sm focus:outline-none disabled:cursor-not-allowed group"
              >
                <Plus className="w-4 h-4 mr-2 group-hover:text-white" />
                <span className="group-hover:text-white text-[14px]">
                  {t('create_new')}
                </span>
              </GradientButton>
              <nav
                className={cn(
                  'space-y-2',
                  '[&>a:hover]:bg-gray-800 [&>a]:cursor-pointer [&>a]:gap-5',
                )}
              >
                <ListItem
                  onClick={() => {
                    window.open('/pricing', '_blank')
                    // add mixpanel event
                    sendToMixpanel(
                      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_PROFILE_MENU_SUBSCRIPTIONS,
                      { project_id: currentProjectId },
                      user?._id.toString() || '',
                    )
                  }}
                >
                  <CreditCardIcon className="size-4" />
                  <span className="text-sm">
                    {tProfileMenu('subscriptions')}
                  </span>
                </ListItem>

                <ListItem
                  onClick={() => window.open('/settings/connect', '_blank')}
                >
                  <Store className="size-4" />
                  <span className="text-sm">{tProfileMenu('my-store')}</span>
                </ListItem>
                <ListItem
                  onClick={() => setIsProjectListOpen(!isProjectListOpen)}
                  className="sticky top-0 z-10"
                >
                  <div className="flex-1 flex gap-5">
                    <FolderOpenDot className="size-4" />
                    <span className="text-sm">
                      {tProfileMenu('my-projects')}
                    </span>
                  </div>
                  <ChevronUpIcon
                    className={cn('size-4', {
                      'rotate-180': !isProjectListOpen,
                    })}
                  />
                </ListItem>
                {isProjectListOpen && (
                  <ProjectList
                    maxColumns={1}
                    currentProjectId={currentProjectId}
                    showSkeletonHeader={false}
                    linkToNewTab={true}
                  />
                )}
                <ListItem onClick={() => window.open('/domains', '_blank')}>
                  <Globe2Icon className="size-4" />
                  <span className="text-sm">{tProfileMenu('domains')}</span>
                </ListItem>
                <div className="bg-black/10 h-px" />
                <ListItem
                  onClick={() => {
                    window.open(EMAIL_URL, '_blank')
                    // add mixpanel event
                    sendToMixpanel(
                      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_PROFILE_MENU_CONTACT_SUPPORT,
                      { project_id: currentProjectId },
                      user?._id.toString() || '',
                    )
                  }}
                >
                  <MailIcon className="size-4" />
                  <span className="text-sm">
                    {tProfileMenu('contact-support')}
                  </span>
                </ListItem>
                <div className="bg-black/10 h-px" />
                {/* <ListItem onClick={() => window.open(ROADMAP_URL, '_blank')}>
                  <MapIcon className="size-4" />
                  <span className="text-sm">{tProfileMenu('roadmap')}</span>
                </ListItem>
                <ListItem
                  onClick={() => window.open(FEATUREBASE_URL, '_blank')}
                >
                  <MessageSquareIcon className="size-4" />
                  <span className="text-sm">{tProfileMenu('feedback')}</span>
                </ListItem>
                <ListItem onClick={() => window.open(CHANGELOG_URL, '_blank')}>
                  <ClipboardListIcon className="size-4" />
                  <span className="text-sm">{tProfileMenu('changelog')}</span>
                </ListItem>
                */}
                <ListItem
                  onClick={() => {
                    window.open(HELP_URL, '_blank')
                    // add mixpanel event
                    sendToMixpanel(
                      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_PROFILE_MENU_HELP_CENTER,
                      { project_id: currentProjectId },
                      user?._id.toString() || '',
                    )
                  }}
                >
                  <HelpCircleIcon className="size-4" />
                  <span className="text-sm">{tProfileMenu('help-center')}</span>
                </ListItem>
                <ListItem onClick={onLogout}>
                  <LogOutIcon className="size-4" />
                  <span className="text-sm">{tProfileMenu('logout')}</span>
                </ListItem>
              </nav>
            </div>
          </ScrollArea>
        </div>
      </div>
    </>
  )
}
